import { useEffect, useState, useRef } from "react";

import useData from "../../hooks/useData";
import useLocalization from "../../hooks/useLocalization";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Typography,
} from "@mui/material";

const SyndicatedTerritoriesSelect = ({ value, onChange, filter }) => {
  const { executeAction } = useData();
  const { getLabel } = useLocalization();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempValue, setTempValue] = useState([]);
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    getData();
    hasAutoSelectedRef.current = false; // Reset when filter changes
  }, [filter]);

  useEffect(() => {
    // Mark that we've loaded options, but DON'T auto-select
    // If filter has no explicit selection, it means user hasn't selected any
    // which should show all non-syndicated locations too
    if (
      !hasAutoSelectedRef.current &&
      Array.isArray(options) &&
      options.length > 0
    ) {
      hasAutoSelectedRef.current = true;
    }
  }, [options]);

  const getData = async () => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Location",
        action: "getAvailableSyndicatedTerritories",
        data: { filter: filter || {} },
        displayMessage: false,
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setOptions(results.data);
      } else {
        setOptions([]);
      }
    } catch (err) {
      console.error("Error fetching syndicated territories:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (event) => {
    setTempValue(Array.isArray(value) ? [...value] : []);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempValue([]);
  };

  const handleApply = () => {
    onChange(tempValue);
    handleClose();
  };

  const handleToggle = (territoryId) => {
    const newValue = Array.isArray(tempValue) ? [...tempValue] : [];
    const index = newValue.indexOf(territoryId);

    if (index === -1) {
      newValue.push(territoryId);
    } else {
      newValue.splice(index, 1);
    }

    setTempValue(newValue);
  };

  const handleSelectAll = () => {
    const allIds = options.map((t) => t.id);
    setTempValue(allIds);
  };

  const handleClearAll = () => {
    setTempValue([]);
  };

  const open = Boolean(anchorEl);
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const showingAll = selectedCount === 0;

  return (
    <Box sx={{ width: "200px" }}>
      <Box sx={{ paddingLeft: "6px", paddingRight: "6px" }}>
        <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
          {getLabel("syndicatedTerritories")}
        </Typography>
      </Box>
      <Button
        onClick={handleOpen}
        fullWidth
        size="small"
        variant="outlined"
        sx={{
          textTransform: "none",
          fontSize: "12px",
          padding: "6px",
          justifyContent: "flex-start",
          color: "#000000",
          borderColor: "#000000",
          "&:hover": {
            borderColor: "#000000",
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "12px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {showingAll ? "All" : `${selectedCount} selected`}
        </Typography>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: "350px" }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              size="small"
              onClick={handleSelectAll}
              disabled={options.length === 0}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={handleClearAll}
              variant="outlined"
            >
              Clear
            </Button>
          </Box>

          <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
            {loading ? (
              <Typography color="textSecondary">Loading...</Typography>
            ) : options.length === 0 ? (
              <Typography color="textSecondary">
                No syndicated territories found
              </Typography>
            ) : (
              options.map((territory) => (
                <FormControlLabel
                  key={territory.id}
                  control={
                    <Checkbox
                      checked={
                        Array.isArray(tempValue) &&
                        tempValue.includes(territory.id)
                      }
                      onChange={() => handleToggle(territory.id)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {territory.name}
                      <Typography
                        variant="caption"
                        sx={{ ml: 1, color: "text.secondary" }}
                      >
                        ({territory.locationCount} locations)
                      </Typography>
                    </Typography>
                  }
                  sx={{ display: "flex", mb: 1 }}
                />
              ))
            )}
          </Box>

          <Box
            sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}
          >
            <Button
              size="small"
              onClick={handleClose}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleApply}
              variant="contained"
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default SyndicatedTerritoriesSelect;
